import * as React from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";

import { RiEyeLine, RiEyeOffLine, RiArrowRightLine } from "react-icons/ri";

import Textfield from "../../Components/Textfield";
import Button from "../../Components/Button";
import StandardLink from "../../Components/StandardLink";

import { useLoader } from "../../Context/LoaderContext";
import { useConnectedUser } from "../../Context/ConnectedUserContext";
import { useToast } from "../../Context/ToastContext";

import "./Login.scss";

const initialFieldLogin = {
  login: "",
  password: "",
};

const initialOtpState = {
  otp_code: "",
};

const LoginEspace = () => {
  const [fieldLogin, setFieldLogin] = React.useState(initialFieldLogin);
  const [fieldErrorMessages, setFieldErrorMessages] = React.useState(initialFieldLogin);
  const [isVisible, setIsVisible] = React.useState(false);
  const [captchaDone, setIsCaptachDone] = React.useState(false);
  const [tokenRecaptcha, setTokenRecaptcha] = React.useState(null);
  const captchaRef = React.useRef(null);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const captchaSize = windowWidth > 480 ? "normal" : "compact";

  // États pour l'OTP
  const [showOtpStep, setShowOtpStep] = React.useState(false);
  const [otpData, setOtpData] = React.useState(initialOtpState);
  const [otpErrorMessage, setOtpErrorMessage] = React.useState("");
  const [keyOp, setKeyOp] = React.useState(null);

  const navigate = useNavigate();
  const { setIsLoading } = useLoader();
  const { updateToken, updateUser, updateisFirstTimeConnect } = useConnectedUser();
  const { addToast } = useToast();

  const handleChangeField = (e, key) => {
    setFieldLogin((prev) => ({
      ...prev,
      [key]: e.target.value,
    }));
    setFieldErrorMessages(initialFieldLogin);
  };

  const handleChangeOtp = (e) => {
    setOtpData({ otp_code: e.target.value });
    setOtpErrorMessage("");
  };

  // Étape 1 : Soumettre login + password
  const handleSubmit = (ev) => {
    ev.preventDefault();

    setIsLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/login`, {
        login: fieldLogin.login,
        password: fieldLogin.password,
        grt: tokenRecaptcha,
        imei: "web-client", // ou récupérer un identifiant unique du navigateur
        os: "web",
      })
      .then((response) => {
        setIsLoading(false);

        if (response.data.status) {
          // Réponse avec OTP envoyé
          setKeyOp(response.data?.data?.otp_token);
          setShowOtpStep(true);
          
          addToast({
            variant: "success",
            message: response.data.data.message || "Code OTP envoyé par SMS",
            duration: 5000,
          });
        }
      })
      .catch((error) => {
        setIsLoading(false);
        captchaRef.current.reset();
        setIsCaptachDone(false);
        
        if (error.response?.data?.errors) {
          setFieldErrorMessages(error.response.data.errors);
        } else {
          addToast({
            variant: "danger",
            message: error.response?.data?.message || "Identifiant ou mot de passe incorrect. Veuillez réessayer.",
          });
        }
      });
  };

  // Étape 2 : Vérifier l'OTP
  const handleVerifyOtp = (e) => {
    e.preventDefault();

    if (!otpData.otp_code || otpData.otp_code.length !== 6) {
      setOtpErrorMessage("Veuillez saisir un code à 6 chiffres");
      return;
    }

    setIsLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/verify-otp`, {
        otp_token: keyOp,
        otp_code: otpData.otp_code,
      })
      .then((response) => {
        setIsLoading(false);

        if (response.data.status) {
          updateToken(response.data.data.access_token);
          updateUser(response.data.data.client);

          if (response.data.data.client.first_time_connected === true) {
            updateisFirstTimeConnect(response.data.data.client.id);

            addToast({
              variant: "warning",
              isDismissible: true,
              message: "Merci de changer votre mot de passe",
              duration: 5000,
            });
            navigate("/edit-account", {
              state: { tabId: "security" },
            });
          } else {
            addToast({
              variant: "success",
              message: "Connexion réussie",
            });
            navigate("/");
          }
        }
      })
      .catch((error) => {
        setIsLoading(false);

        if (error.response?.data?.errors?.otp_code) {
          setOtpErrorMessage(error.response.data.errors.otp_code);
        } else {
          addToast({
            variant: "danger",
            message: error.response?.data?.message || "Code OTP invalide",
          });
        }
      });
  };

  // Renvoyer l'OTP
  const handleResendOtp = () => {
    setIsLoading(true);
    axios
      .post(`${process.env.REACT_APP_API_URL}/resend-login-otp`, {
        otp_token: keyOp,
      })
      .then((response) => {
        setIsLoading(false);

        if (response.data.status) {
          setKeyOp(response.data?.data?.otp_token);
          addToast({
            variant: "success",
            message: response.data.message || "Code OTP renvoyé par SMS",
          });
          setOtpData(initialOtpState);
          setOtpErrorMessage("");
        }
      })
      .catch((error) => {
        setIsLoading(false);

        addToast({
          variant: "danger",
          message: error.response?.data?.message || "Erreur lors du renvoi du code",
        });
      });
  };

  const ShowPassword = () => {
    setIsVisible((prev) => !prev);
  };

  const onChangeCaptcha = (value) => {
    setIsCaptachDone(true);
    setTokenRecaptcha(value);
  };

  const handleBackToLogin = () => {
    setShowOtpStep(false);
    setOtpData(initialOtpState);
    setOtpErrorMessage("");
    setKeyOp(null);
    captchaRef.current.reset();
    setIsCaptachDone(false);
  };

  // Affichage du formulaire OTP
  if (showOtpStep) {
    return (
      <>
        <h1 className="title">Vérification OTP</h1>
        <p className="sub-title">
          Un code de vérification a été envoyé par SMS à votre numéro.
        </p>
        <form className="login-form" onSubmit={handleVerifyOtp}>
          <Textfield
            label="Code OTP"
            name="otp_code"
            type="text"
            maxLength="6"
            pattern="\d{6}"
            placeholder="Saisissez le code à 6 chiffres"
            size="large"
            autoComplete="off"
            value={otpData.otp_code}
            onChange={handleChangeOtp}
            hasError={otpErrorMessage}
            errorMessage={otpErrorMessage}
            required
          />

          <div className="wrap_actions_form mb-3">
            <Button
              type="button"
              variant="dark"
              view="outline"
              size="lg"
              onClick={handleResendOtp}
            >
              Renvoyer le code
            </Button>
            <Button type="submit" size="lg" isFullyRounded>
              Vérifier <RiArrowRightLine />
            </Button>
          </div>

          <div className="text-center mt-3">
            <StandardLink variant="dark" size="lg" onClick={handleBackToLogin}>
              ← Retour à la connexion
            </StandardLink>
          </div>
        </form>
      </>
    );
  }

  // Affichage du formulaire de connexion initial
  return (
    <>
      <h1 className="title">Vous êtes client</h1>
      <p className="sub-title">
        Accédez à votre compte et suivez toutes vos opérations en ligne
      </p>
      <form className="login-form" onSubmit={handleSubmit}>
        <Textfield
          label="Login"
          name="login"
          type="text"
          placeholder="Veuillez insérer votre Code Client"
          size="large"
          autoComplete="on"
          value={fieldLogin.login}
          onChange={(e) => handleChangeField(e, "login")}
          hasError={fieldErrorMessages.login}
          errorMessage={fieldErrorMessages.login}
          required
        />
        <Textfield
          label="Mot de passe"
          name="password"
          type={isVisible ? "text" : "password"}
          placeholder="Veuillez insérer votre mot de passe"
          size="large"
          rightIcon={
            isVisible ? (
              <RiEyeLine onClick={ShowPassword} />
            ) : (
              <RiEyeOffLine onClick={ShowPassword} />
            )
          }
          autoComplete="off"
          value={fieldLogin.password}
          onChange={(e) => handleChangeField(e, "password")}
          hasError={fieldErrorMessages.password}
          errorMessage={fieldErrorMessages.password}
          required
        />

        <div className="captcha-box">
          <ReCAPTCHA
            key={captchaSize}
            sitekey={process.env.REACT_APP_SITE_KEY}
            ref={captchaRef}
            onChange={onChangeCaptcha}
            size={captchaSize}
          />
        </div>

        <div className="wrap_actions_form mb-3">
          <StandardLink variant="dark" size="lg" to="/forget-password">
            Mot de passe oublié
          </StandardLink>
          <Button
            type="submit"
            size="lg"
            disabled={!captchaDone}
            isFullyRounded
          >
            Se connecter <RiArrowRightLine />
          </Button>
        </div>
      </form>
    </>
  );
};

export default LoginEspace;